/**
 * Sector Operations — Client Links Service.
 * R39 E4.a + E4.b (plan docs/plans/sector-operations.md SO-P274, SO-D36, SO-D47).
 */

import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import type {
  IssueClientLinksInput,
  ReissueClientLinkInput,
  RevokeClientLinkInput,
  ListClientLinksInput,
  GetClientLinkEventsInput,
  GetClientLinkInfoInput,
  PrepareClientLinkOtpInput,
  OpenClientLinkInput,
  OpenClientLinkResult,
  OpenClientPortalApprovalInput,
  DecideClientPortalApprovalInput,
  SetClientUatResultInput,
  OpsClientLink,
  OpsClientLinkEvent,
  OutsideApproval,
  OutsideApprover,
} from '../types/ops'

// ---------------------------------------------------------------------------
// 1. Staff callables (Workspace admins only, default functions)
// ---------------------------------------------------------------------------

export async function issueClientLinks(
  input: IssueClientLinksInput,
): Promise<{
  success: boolean
  links: Array<OpsClientLink & { token?: string }>
}> {
  return callable('issueClientLinks', input)
}

export async function reissueClientLink(
  input: ReissueClientLinkInput,
): Promise<{
  success: boolean
  link: OpsClientLink
  token: string
}> {
  return callable('reissueClientLink', input)
}

export async function revokeClientLink(
  input: RevokeClientLinkInput,
): Promise<{
  success: boolean
  link: OpsClientLink
}> {
  return callable('revokeClientLink', input)
}

export async function listClientLinks(
  input: ListClientLinksInput,
): Promise<{
  links: OpsClientLink[]
  events: OpsClientLinkEvent[]
}> {
  return callable('listClientLinks', input)
}

export async function getClientLinkEvents(
  input: GetClientLinkEventsInput,
): Promise<{
  events: OpsClientLinkEvent[]
}> {
  return callable('getClientLinkEvents', input)
}

// ---------------------------------------------------------------------------
// 2. Outside link & portal callables (Outside Approvals App instance)
// ---------------------------------------------------------------------------

export async function getClientLinkInfo(
  input: GetClientLinkInfoInput,
): Promise<{
  workspaceName: string
  subjectTitle: string
  name: string
  phoneMasked: string
}> {
  return callable('getClientLinkInfo', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function prepareClientLinkOtp(
  input: PrepareClientLinkOtpInput,
): Promise<{
  success: boolean
  attemptsRemaining: number
}> {
  return callable('prepareClientLinkOtp', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function openClientLink(
  input: OpenClientLinkInput,
): Promise<OpenClientLinkResult> {
  return callable('openClientLink', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function openClientPortalApproval(
  input: OpenClientPortalApprovalInput,
): Promise<{
  approval: OutsideApproval
  approver: OutsideApprover
}> {
  return callable('openClientPortalApproval', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function decideClientPortalApproval(
  input: DecideClientPortalApprovalInput,
): Promise<{
  approval: OutsideApproval
  approver: OutsideApprover
}> {
  return callable('decideClientPortalApproval', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function setClientUatResult(
  input: SetClientUatResultInput,
): Promise<{
  success: boolean
  round: unknown
}> {
  return callable('setClientUatResult', input, {
    functions: getApprovalsFunctions(),
  })
}
